import type { BloomMenuTheme } from '@iheartjane/data-access';
import type {
  CategoryTabPosition,
  CategoryTabStyle,
  ProductCardCtaIcon,
  ProductCardCtaStyle,
  ProductCardHover,
  ProductCardSize,
  SearchPosition,
} from '@iheartjane/types';

export const buildCustomerRatingProperties = ({
  rating,
  reservationId,
}: {
  rating: number;
  reservationId: number;
}) => ({
  cartId: reservationId,
  'Star Rating': rating,
});

interface CustomComponentSettings {
  categoryTabPosition: CategoryTabPosition;
  categoryTabStyle: CategoryTabStyle;
  productCardBorder: boolean;
  /** Null when the CTA style is `card_bottom_full`, which renders no icon. */
  productCardCtaIcon: ProductCardCtaIcon | null;
  productCardCtaStyle: ProductCardCtaStyle;
  productCardHover: ProductCardHover;
  productCardSize: ProductCardSize;
  searchPosition: SearchPosition;
}

export interface CustomComponentProperties extends CustomComponentSettings {
  /** True when any other setting here differs from the platform default. */
  customized: boolean;
}

/**
 * The `??` fallbacks are the platform defaults, kept in sync with the API's
 * DEFAULT_THEME (api/app/lib/bloom/theme_type.rb). The API stores only a sparse
 * delta and re-merges defaults on read, so a resolved value differing from its
 * fallback here means the operator customized it.
 */
const resolveCustomComponentSettings = (
  bloom: BloomMenuTheme['bloom']
): CustomComponentSettings => {
  const productCardCtaStyle =
    bloom?.product_cards?.cta_style ?? 'card_bottom_full';

  return {
    categoryTabPosition: bloom?.category_tabs?.position ?? 'above_carousel',
    categoryTabStyle: bloom?.category_tabs?.style ?? 'icon',
    productCardBorder: bloom?.product_cards?.card_border ?? false,
    productCardCtaIcon:
      productCardCtaStyle === 'card_bottom_full'
        ? null
        : (bloom?.product_cards?.cta_icon ?? 'add'),
    productCardCtaStyle,
    productCardHover: bloom?.product_cards?.hover ?? 'shadow',
    productCardSize: bloom?.product_cards?.card_size ?? 'medium',
    searchPosition: bloom?.search?.position ?? 'split',
  };
};

const DEFAULT_CUSTOM_COMPONENT_SETTINGS = resolveCustomComponentSettings(null);

/**
 * Projects the menu theme's custom-component settings onto a single Mixpanel
 * property. Values are always resolved (defaults included) so the shape is
 * identical on every event, and Mixpanel can break down or filter on a setting
 * via arrow notation — e.g. `customComponents → productCardCtaStyle`.
 *
 * Colors, fonts, and the logo are deliberately excluded: they aren't variants
 * to compare, and fonts carry a whole map of CDN URLs.
 *
 * Values must stay lowercase — `normalizeMixpanelPropertyValues` does not
 * recurse into objects, so nothing here gets downcased for us.
 */
export const buildCustomComponentProperties = (theme?: BloomMenuTheme) => {
  const settings = resolveCustomComponentSettings(theme?.bloom);

  const customized = Object.entries(settings).some(
    ([key, value]) =>
      value !==
      DEFAULT_CUSTOM_COMPONENT_SETTINGS[key as keyof CustomComponentSettings]
  );

  return {
    customComponents: {
      ...settings,
      customized,
    } satisfies CustomComponentProperties,
  };
};
